<template>
  <VCard class="mt-2 pa-4 pb-0" flat>
    <div class="d-flex flex-column ga-3">
      <span ref="participantLabel" class="label text-h6 invisible-measure"
        >{{ t('Participant') }}:</span
      >
      <p class="ml-1">
        <span
          class="label text-h6"
          :style="{ minWidth: `${longestLabelWidth}px`, display: 'inline-block' }"
        >
          {{ t('Patient') }}:
        </span>
        <span class="value">
          <span class="value">{{ getFormattedPatientName(cases[0].patient, false) }}</span>
          <span class="mx-1">·</span>
          <span v-if="cases[0].patient.dateOfBirth" class="font-weight-medium">
            {{ getFormattedAge(cases[0].patient) }}
            <span class="mx-1">·</span>
          </span>
          <span class="font-weight-medium">{{ getFormattedCaseId(cases[0].externalId) }}</span>
          <span class="mx-1">·</span>
          <span class="font-weight-medium">
            {{ cases[0].ward?.name }}
            <span class="mx-1">·</span>
          </span>
          <span class="font-weight-medium">{{ cases[0].hospital?.name }}</span>
        </span>
      </p>

      <p v-for="(caze, idx) in cases.slice(1)" :key="'case-' + idx" class="patient-line ml-1">
        <span
          class="value"
          :style="{ marginLeft: `${longestLabelWidth}px`, display: 'inline-block' }"
        >
          <span class="value">{{ getFormattedPatientName(caze.patient, false) }}</span>
          <span class="mx-1">·</span>
          <span v-if="caze.patient.dateOfBirth" class="font-weight-medium">
            {{ getFormattedAge(caze.patient) }}
            <span class="mx-1">·</span>
          </span>
          <span class="font-weight-medium">{{ getFormattedCaseId(caze.externalId) }}</span>
          <span class="mx-1">·</span>
          <span class="font-weight-medium"
            >{{ caze.ward?.name }}
            <span class="mx-1">·</span>
          </span>
          <span class="font-weight-medium">{{ caze.hospital?.name }}</span>
        </span>
      </p>

      <p v-if="hasConsultationDetails || customFields.length">
        <VBtn
          class="text-h6 p-0"
          variant="text"
          size="small"
          color="primary"
          :ripple="false"
          @click="$emit('update:show-details', !showDetails)"
        >
          {{ showDetails ? t('Hide consultation details') : t('Show consultation details') }}
          <VIcon
            :icon="showDetails ? 'arrow_drop_down' : 'chevron_right'"
            size="small"
            class="ml-1"
          />
        </VBtn>
      </p>
    </div>

    <VExpandTransition>
      <div v-if="showDetails" class="ml-6 pt-2">
        <template v-if="details.type === 'scheduled'">
          <p v-if="details.comment" class="p-1 flex">
            <span class="text-h6">{{ t('Comment (optional)') }}:</span>
            <span class="value mx-1 whitespace-pre-line">{{ details.comment }}</span>
          </p>

          <p v-if="details.medicalHistory" class="p-1 flex">
            <span class="text-h6">{{ t('Medical History') }}:</span>
            <span class="value mx-1 whitespace-pre-line">{{ details.medicalHistory }}</span>
          </p>

          <p v-if="details.conferenceTitle" class="p-1 flex">
            <span class="text-h6">{{ t('Conference Title') }}:</span>
            <span class="value mx-1 whitespace-pre-line">{{ details.conferenceTitle }}</span>
          </p>

          <p v-if="details.conferenceDescription" class="p-1 flex">
            <span class="text-h6">{{ t('Conference Topic (optional)') }}:</span>
            <span class="value mx-1 whitespace-pre-line">{{ details.conferenceDescription }}</span>
          </p>
        </template>

        <template v-else-if="details.type === 'direct'">
          <p class="p-1 flex">
            <span class="text-h6">{{ t('Diagnosis') }}:</span>
            <span class="value mx-1 whitespace-pre-line">{{ details.diagnosis }}</span>
          </p>
          <p class="p-1 flex">
            <span class="text-h6">{{ t('Question') }}:</span>
            <span class="value mx-1 whitespace-pre-line">{{ details.question }}</span>
          </p>
          <p v-if="details.medicalHistory" class="p-1 flex">
            <span class="text-h6">{{ t('Medical history') }}:</span>
            <span class="value mx-1 whitespace-pre-line">{{ details.medicalHistory }}</span>
          </p>
          <p v-if="details.therapy" class="p-1 flex">
            <span class="text-h6">{{ t('Therapy') }}:</span>
            <span class="value mx-1 whitespace-pre-line"> {{ details.therapy }}</span>
          </p>
        </template>

        <p
          v-for="field in customFields"
          :key="field.id"
          class="p-1 flex"
          data-testid="custom-field"
        >
          <span class="text-h6">{{ field.label }}:</span>
          <span class="value mx-1 whitespace-pre-line">{{ field.value }}</span>
        </p>
      </div>
    </VExpandTransition>
  </VCard>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, toRefs } from 'vue'
import type { CaseDTO, CustomConsultationFieldDTO } from '@/generated'
import { useI18n } from 'vue-i18n'
import useFormattedPatientDetails from '@/composables/useFormattedPatientDetails'
import useFormattedCaseDetails from '@/composables/useFormattedCaseDetails'

const { t } = useI18n()
const { getFormattedPatientName, getFormattedAge } = useFormattedPatientDetails()
const { getFormattedCaseId } = useFormattedCaseDetails()

interface DirectConsultationDetails {
  type: 'direct'
  diagnosis: string
  question: string
  medicalHistory?: string | null
  therapy?: string | null
}
interface ScheduledConsultationDetails {
  type: 'scheduled'
  comment?: string | null
  medicalHistory?: string | null
  conferenceTitle?: string | null
  conferenceDescription?: string | null
}

const props = defineProps<{
  cases: CaseDTO[]
  details: DirectConsultationDetails | ScheduledConsultationDetails
  showDetails: boolean
  customFields: CustomConsultationFieldDTO[]
}>()

const { cases, details, showDetails } = toRefs(props)

const participantLabel = ref<HTMLElement | null>(null)
const longestLabelWidth = ref(0)

onMounted(async () => {
  await nextTick()
  if (participantLabel.value) {
    longestLabelWidth.value = participantLabel.value.offsetWidth + 5
  }
})

const hasConsultationDetails = computed(() => {
  const { type: _type, ...rest } = props.details
  return Object.values(rest).some((v) => v && v !== '')
})
</script>
<style scoped>
.label {
  white-space: nowrap;
}
.invisible-measure {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  left: -9999px;
}
.value {
  font-size: 1rem;
}

::v-deep(.html-embedded p) {
  display: inline;
}

.patient-line {
  padding: 0;
  line-height: 0;
  margin-bottom: 0.8rem;
}
</style>
